import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Download, Lock, PlayCircle, Eye } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import ResourcePreviewModal from '@/components/ResourcePreviewModal';
import EmailCaptureModal from '@/components/EmailCaptureModal';
import { resources, resourceCategories } from '@/mockData';
import { trackResourceDownload } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ResourceCategory = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [previewResource, setPreviewResource] = useState(null);
  const [emailCaptureResource, setEmailCaptureResource] = useState(null);

  const categoryInfo = resourceCategories.find(c => c.id === category);
  const categoryResources = resources[category] || [];

  if (!categoryInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
          <Button onClick={() => navigate('/resources')}>Back to Resources</Button>
        </div>
      </div>
    );
  }

  const handlePremiumClick = (resource) => {
    setEmailCaptureResource(resource);
  };

  const handleDownload = (resource) => {
    if (!resource.downloadUrl || resource.downloadUrl === '#') {
      alert('Download not available yet');
      return;
    }

    try {
      trackResourceDownload(resource.title, category);
    } catch { /* noop */ }

    // Trigger download
    const downloadLink = `${BACKEND_URL}/api/v4/resources/download/${resource.downloadUrl}`;
    window.open(downloadLink, '_blank');
  };

  const getResourceIcon = (type) => {
    switch(type) {
      case 'Practice Test':
        return <PlayCircle className="w-5 h-5" />;
      case 'PDF':
        return <FileText className="w-5 h-5" />;
      case 'Worksheet':
        return <FileText className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Beginner':
        return { bg: 'rgba(34, 197, 94, 0.15)', text: '#16a34a' };
      case 'Intermediate':
        return { bg: 'rgba(234, 179, 8, 0.15)', text: '#ca8a04' };
      case 'Advanced':
        return { bg: 'rgba(239, 68, 68, 0.15)', text: '#dc2626' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.15)', text: '#6b7280' };
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-stone-50 border-b" style={{borderBottomColor: 'rgba(183, 191, 114, 0.20)', paddingTop: '6rem'}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/resources')}
            className="mb-6 hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Resources
          </Button>
          
          <div className="inline-block mb-4">
            <div className="h-1 w-16" style={{background: `linear-gradient(90deg, ${categoryInfo.color} 0%, #B7BF72 100%)`}}></div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-gray-900">
            {categoryInfo.title} Resources
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            {categoryInfo.description}
          </p>
        </div>
      </div>

      {/* Resources List */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Special Needs Free Access Banner */}
        {category === 'special-needs' && (
          <div className="mb-10 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-green-900">
                Free Access for All Students
              </h3>
            </div>
            <p className="text-green-800 leading-relaxed">
              All Special Needs Support resources are completely free. We believe every student deserves access to quality learning materials.
            </p>
          </div>
        )}
        
        <div className="mb-10">
          <h2 className="text-3xl font-bold mb-2 text-gray-900">Available Resources</h2>
          <p className="text-gray-600">
            {categoryResources.length} resource{categoryResources.length !== 1 ? 's' : ''} available
          </p>
        </div>

        {categoryResources.length === 0 ? (
          <Card className="p-12 text-center border-2" style={{borderColor: 'rgba(183, 191, 114, 0.20)'}}>
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-2xl font-bold mb-2 text-gray-900">Resources Coming Soon</h3>
            <p className="text-gray-600 mb-6">
              We're preparing high-quality materials for this subject. Check back soon!
            </p>
            <Button onClick={() => navigate('/resources')}>
              Browse Other Subjects
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {categoryResources.map((resource) => (
              <Card
                key={resource.id}
                className="p-6 hover:shadow-xl transition-all duration-300 border-2"
                style={{borderColor: resource.isPremium ? 'rgba(183, 191, 114, 0.30)' : 'rgba(0, 108, 105, 0.15)'}}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{background: resource.isPremium ? 'rgba(183, 191, 114, 0.15)' : 'rgba(0, 108, 105, 0.10)'}}
                      >
                        {getResourceIcon(resource.type)}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                          {resource.title}
                          {resource.isPremium && (
                            <Lock className="w-4 h-4 text-yellow-600" />
                          )}
                          {resource.microLabel && (
                            <Badge 
                              className="text-[10px] font-bold px-2 py-0.5"
                              style={{
                                background: 'linear-gradient(135deg, #B7BF72 0%, #a8b565 100%)',
                                color: '#000000'
                              }}
                            >
                              {resource.microLabel}
                            </Badge>
                          )}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge 
                            className="text-xs font-semibold"
                            style={{
                              background: resource.isPremium ? '#B7BF72' : '#006C69',
                              color: '#FFFFFF'
                            }}
                          >
                            {resource.type}
                          </Badge>
                          <Badge 
                            className="text-xs font-semibold"
                            style={{
                              background: resource.isPremium ? 'rgba(183, 191, 114, 0.20)' : 'rgba(0, 108, 105, 0.15)',
                              color: resource.isPremium ? '#B7BF72' : '#006C69',
                              border: `1px solid ${resource.isPremium ? '#B7BF72' : '#006C69'}`
                            }}
                          >
                            {resource.isPremium ? 'Premium' : 'Free'}
                          </Badge>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-500 font-medium">{resource.fileSize}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <Badge 
                            className="text-xs font-semibold"
                            style={{
                              background: getDifficultyColor(resource.difficulty).bg,
                              color: getDifficultyColor(resource.difficulty).text,
                              border: 'none'
                            }}
                          >
                            {resource.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 leading-relaxed mb-2">
                      {resource.description}
                    </p>
                    
                    {resource.isPremium && (
                      <p className="text-sm text-gray-500 italic">
                        Unlock with subscription (coming soon)
                      </p>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <div className="flex flex-col gap-2">
                      {/* Preview Button */}
                      {resource.previewContent && (
                        <Button
                          onClick={() => setPreviewResource(resource)}
                          variant="outline"
                          className="font-semibold"
                          style={{
                            borderColor: '#006C69',
                            color: '#006C69'
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                      )}
                      
                      {/* Action Button */}
                      {resource.isPremium ? (
                        <Button
                          onClick={() => handlePremiumClick(resource)}
                          className="font-semibold"
                          style={{
                            background: '#B7BF72',
                            color: '#000000'
                          }}
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Get Access
                        </Button>
                      ) : resource.type === 'Simulated Exam' ? (
                        <Button
                          onClick={() => navigate(`/exam/${resource.examId}`)}
                          className="font-semibold w-full"
                          style={{
                            background: 'linear-gradient(135deg, #006C69 0%, #004d4a 100%)',
                            color: '#FFFFFF'
                          }}
                        >
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Start Full Exam
                        </Button>
                      ) : resource.type === 'Practice Test' ? (
                        <Button
                          onClick={() => {
                            const testMapping = {
                              // VCE
                              'VCE English Practice Exam 2024': 'vce-english-practice',
                              'VCE Text Response Skills': 'vce-text-response-practice',
                              // Selective Entry
                              'Mathematics Practice Test Set 1': 'selective-math-practice',
                              'Selective Entry Reading Comprehension': 'selective-reading-practice',
                              'Selective Entry Logical Reasoning': 'selective-reasoning-practice',
                              // IELTS
                              'Academic Writing Task 2 Templates': 'ielts-practice-test',
                              'IELTS Reading Practice': 'ielts-reading-practice',
                              'IELTS Writing Skills': 'ielts-writing-practice',
                              'IELTS Listening Skills': 'ielts-listening-practice',
                              'IELTS Speaking Skills': 'ielts-speaking-practice',
                              // GMAT
                              'Data Sufficiency Practice Test': 'gmat-data-sufficiency',
                              'GMAT Quantitative Reasoning': 'gmat-quant-practice',
                              'GMAT Critical Reasoning': 'gmat-verbal-practice',
                              // GRE
                              'GRE Vocabulary Flashcards (500 words)': 'gre-vocabulary-test',
                              'GRE Quantitative Reasoning': 'gre-quant-practice',
                              'GRE Verbal Reasoning': 'gre-verbal-practice',
                              // SAT
                              'SAT Math Practice Test': 'sat-math-test',
                              'SAT Reading Comprehension': 'sat-reading-practice',
                              'SAT Writing & Language': 'sat-writing-practice',
                              // EAL
                              'EAL Reading Skills': 'eal-reading-practice',
                              'EAL Writing Skills': 'eal-writing-practice',
                              'EAL Grammar Essentials': 'eal-grammar-practice',
                              // Special Needs
                              'Reading Comprehension Basics': 'special-needs-reading',
                              'Foundational Math Skills': 'special-needs-math',
                              'Vocabulary Practice': 'special-needs-vocabulary',
                              'Step-by-Step Reasoning': 'special-needs-reasoning',
                            };
                            const testId = testMapping[resource.title];
                            if (testId) {
                              navigate(`/test/${testId}`);
                            } else {
                              alert('Test not available yet');
                            }
                          }}
                          className="font-semibold"
                          style={{
                            background: '#006C69',
                            color: '#FFFFFF'
                          }}
                        >
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Start Test
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleDownload(resource)}
                          className="font-semibold"
                          style={{
                            background: '#006C69',
                            color: '#FFFFFF'
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Practice Test Placeholder */}
        {categoryResources.some(r => r.type === 'Practice Test') && (
          <Card className="mt-10 p-8 bg-gradient-to-br from-gray-50 to-white border-2" style={{borderColor: 'rgba(0, 108, 105, 0.20)'}}>
            <div className="flex items-center gap-4">
              <PlayCircle className="w-12 h-12" style={{color: '#006C69'}} />
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Interactive Practice Tests Available</h3>
                <p className="text-gray-600">
                  Take comprehensive practice tests with instant scoring, detailed answer explanations, and performance feedback. Click "Start Test" on any Practice Test above to begin your preparation.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Footer />
      <FloatingActionButtons />
      
      {/* Resource Preview Modal */}
      <ResourcePreviewModal
        resource={previewResource}
        isOpen={!!previewResource}
        onClose={() => setPreviewResource(null)}
      />
      
      {/* Email Capture Modal */}
      <EmailCaptureModal
        resource={emailCaptureResource}
        isOpen={!!emailCaptureResource}
        onClose={() => setEmailCaptureResource(null)}
      />
    </div>
  );
};

export default ResourceCategory;
